import type {
  CreateOperatorGroupDto,
  UpdateOperatorGroupDto,
} from '@data-delivery/permission-ui-api';
import { operatorRolesApi } from '@data-delivery/permission-ui-api';
import { useMutation, useQuery } from '@tanstack/react-query';
import { permissionQueryClient as qc } from '../query-client';

export const operatorGroupKeys = {
  all: ['operator-groups'] as const,
  list: () => [...operatorGroupKeys.all, 'list'] as const,
  detail: (id: string) => [...operatorGroupKeys.all, 'detail', id] as const,
};

type OperatorGroupEntity = NonNullable<
  Awaited<ReturnType<typeof operatorRolesApi.getAllOperatorGroups>>['data']
>[number];

export function useOperatorGroupsQuery() {
  return useQuery({
    queryKey: operatorGroupKeys.list(),
    async queryFn() {
      const { data } = await operatorRolesApi.getAllOperatorGroups();
      return data ?? [];
    },
  });
}

export function useCreateOperatorGroupMutation() {
  return useMutation<
    OperatorGroupEntity | undefined,
    unknown,
    CreateOperatorGroupDto,
    { previousList: OperatorGroupEntity[]; optimisticId: string }
  >({
    mutationKey: [...operatorGroupKeys.all, 'create'],
    async mutationFn(dto) {
      const { data } = await operatorRolesApi.createAOperatorGroup({ createOperatorGroupDto: dto });
      return data ?? undefined;
    },
    async onMutate(dto) {
      await qc.cancelQueries({ queryKey: operatorGroupKeys.list() });
      const previousList = qc.getQueryData<OperatorGroupEntity[]>(operatorGroupKeys.list()) || [];
      const optimistic: OperatorGroupEntity = {
        id: `temp-${Date.now()}`,
        name: dto.name,
        businessGroupRoles: dto.businessGroupRoles ?? [],
        lastModified: Date.now(),
      };
      qc.setQueryData(operatorGroupKeys.list(), [...previousList, optimistic]);
      return { previousList, optimisticId: optimistic.id };
    },
    onError(_error, _dto, context) {
      if (context?.previousList) {
        qc.setQueryData(operatorGroupKeys.list(), context.previousList);
      }
    },
    onSuccess(serverEntity, _dto, context) {
      if (!serverEntity || !context?.optimisticId) {
        return;
      }

      const list = qc.getQueryData<OperatorGroupEntity[]>(operatorGroupKeys.list()) || [];
      qc.setQueryData(
        operatorGroupKeys.list(),
        list.map(g => (g.id === context.optimisticId ? serverEntity : g)),
      );
    },
    onSettled() {
      qc.invalidateQueries({ queryKey: operatorGroupKeys.list() });
    },
  });
}

export function useUpdateOperatorGroupMutation(id: string) {
  type UpdateInput = {
    lastModified: number;
    name?: string;
    businessGroupRoles?: UpdateOperatorGroupDto['businessGroupRoles'];
  };
  return useMutation<
    OperatorGroupEntity | undefined,
    unknown,
    UpdateInput,
    { previousList: OperatorGroupEntity[]; previousDetail?: OperatorGroupEntity }
  >({
    mutationKey: [...operatorGroupKeys.all, 'update', id],
    async mutationFn(dto) {
      const payload: UpdateOperatorGroupDto = { lastModified: dto.lastModified };
      if (dto.name !== undefined) {
        payload.name = dto.name;
      }

      if (dto.businessGroupRoles !== undefined) {
        payload.businessGroupRoles = dto.businessGroupRoles;
      }

      const { data } = await operatorRolesApi.updateOperatorGroup({
        id,
        updateOperatorGroupDto: payload,
      });
      return data ?? undefined;
    },
    async onMutate(dto) {
      await qc.cancelQueries({ queryKey: operatorGroupKeys.list() });
      await qc.cancelQueries({ queryKey: operatorGroupKeys.detail(id) });
      const previousList = qc.getQueryData<OperatorGroupEntity[]>(operatorGroupKeys.list()) || [];
      const previousDetail = qc.getQueryData<OperatorGroupEntity>(operatorGroupKeys.detail(id));
      const nextList = previousList.map(g => {
        if (g.id !== id) {
          return g;
        }

        return {
          ...g,
          name: dto.name === undefined ? g.name : dto.name,
          businessGroupRoles:
            dto.businessGroupRoles === undefined ? g.businessGroupRoles : dto.businessGroupRoles,
          lastModified: dto.lastModified,
        };
      });
      qc.setQueryData(operatorGroupKeys.list(), nextList);
      if (previousDetail) {
        qc.setQueryData(operatorGroupKeys.detail(id), {
          ...previousDetail,
          name: dto.name === undefined ? previousDetail.name : dto.name,
          businessGroupRoles:
            dto.businessGroupRoles === undefined
              ? previousDetail.businessGroupRoles
              : dto.businessGroupRoles,
          lastModified: dto.lastModified,
        });
      }

      return { previousList, previousDetail };
    },
    onError(_error, _dto, context) {
      if (context?.previousList) {
        qc.setQueryData(operatorGroupKeys.list(), context.previousList);
      }

      if (context?.previousDetail) {
        qc.setQueryData(operatorGroupKeys.detail(id), context.previousDetail);
      }
    },
    onSuccess(serverEntity) {
      if (!serverEntity) {
        return;
      }

      const list = qc.getQueryData<OperatorGroupEntity[]>(operatorGroupKeys.list()) || [];
      qc.setQueryData(
        operatorGroupKeys.list(),
        list.map(g => (g.id === id ? { ...g, ...serverEntity } : g)),
      );
      const detail = qc.getQueryData<OperatorGroupEntity>(operatorGroupKeys.detail(id));
      if (detail) {
        qc.setQueryData(operatorGroupKeys.detail(id), { ...detail, ...serverEntity });
      }
    },
    onSettled() {
      qc.invalidateQueries({ queryKey: operatorGroupKeys.detail(id) });
      qc.invalidateQueries({ queryKey: operatorGroupKeys.list() });
    },
  });
}

export function useDeleteOperatorGroupMutation() {
  return useMutation<unknown, unknown, string, { previousList: OperatorGroupEntity[] }>({
    mutationKey: [...operatorGroupKeys.all, 'delete'],
    async mutationFn(id: string) {
      const { data } = await operatorRolesApi.deleteOperatorGroupById({ id });
      return data;
    },
    async onMutate(id: string) {
      await qc.cancelQueries({ queryKey: operatorGroupKeys.list() });
      const previousList = qc.getQueryData<OperatorGroupEntity[]>(operatorGroupKeys.list()) || [];
      qc.setQueryData(
        operatorGroupKeys.list(),
        previousList.filter(g => g.id !== id),
      );
      return { previousList };
    },
    onError(_error, _id, context) {
      if (context?.previousList) {
        qc.setQueryData(operatorGroupKeys.list(), context.previousList);
      }
    },
    onSettled() {
      qc.invalidateQueries({ queryKey: operatorGroupKeys.list() });
    },
  });
}
